import { createMemo, createSignal, createEffect, onCleanup, untrack, For, Show } from "solid-js";
import { sourcesStore } from "../../../../store/sourcesStore";
import { defaultChannelsStore } from "../../../../store/defaultChannelsStore";
import { streamingDataService } from "../../../../services/streamingDataService";
import { debug } from "../../../../utils/console";
import {
  computeTwaTackSensorStats,
  computeTwaTackStats,
  formatTwaTackDelta,
  formatTwaTackMeasureValue,
  formatTwaTackValue,
  isTwaTackSensorView,
  listTwaChannels,
  nextTwaTackView,
  summariseRowCoverage,
  TWA_TACK_CHANNELS,
  TWA_TACK_METRIC_LABELS,
  TWA_TACK_SENSOR_LABELS,
  TWA_TACK_WINDOW_MS,
  twaTackUnitFromChannel,
  twaTackViewLabel,
  type TwaTackModeStats,
  type TwaTackSensorModeStats,
  type TwaTackView,
} from "../../../../utils/liveTwaTackStats";

/** Repo-wide tack colours (PORT red / STBD green) — same values as colorGrouping.ts. */
const PORT_COLOR = "#d62728";
const STBD_COLOR = "#2ca02c";

/** The overlay reads one boat only. */
export const DEFAULT_TWA_TACK_SOURCE = "GER";

/**
 * Reload cadence. The window is 5 minutes of merged rows, so this is deliberately slow — matches
 * LiveTable's focus-stats auto refresh.
 */
export const TWA_TACK_REFRESH_MS = 30 * 1000;

/** Past this age the readout is dimmed: the numbers no longer describe the last 5 minutes. */
const TWA_TACK_STALE_MS = TWA_TACK_REFRESH_MS * 3;

/**
 * Narrowing retry windows (ms). The merged endpoint only serves from the server recent store when it
 * *fully* covers the requested range (`recentStore.covers`), so a 5-minute ask returns nothing at all
 * for the first few minutes after the stream server starts. Fall back to shorter windows rather than
 * showing an empty panel.
 */
const TWA_TACK_FALLBACK_WINDOWS_MS = [TWA_TACK_WINDOW_MS, 2 * 60 * 1000, 45 * 1000];

/** Raw merged rows from the last poll, kept so a metric switch re-averages without refetching. */
interface TwaTackRows {
  rows: Record<string, unknown>[];
  /** Window actually served, so the footer never claims 5 minutes it did not get. */
  windowMs: number;
}

/** Either shape the panel can render: both sensors on one metric, or all measures for one sensor. */
type AnyModeStats = TwaTackModeStats | TwaTackSensorModeStats;

interface TwaTackSnapshot {
  view: TwaTackView;
  upwind: AnyModeStats;
  downwind: AnyModeStats;
  /** Short reason the cells are blank, or null when there is something to show. */
  note: string | null;
  windowMs: number;
}

/** Pre-formatted cells, so one table component renders either stats shape. */
interface DisplayRow {
  key: string;
  label: string;
  port: string;
  stbd: string;
  delta: string;
  portCount: number;
  stbdCount: number;
}

function toDisplayRows(stats: AnyModeStats): DisplayRow[] {
  if ('sensor' in stats) {
    return stats.rows.map((row) => ({
      key: row.measure,
      label: row.label,
      port: formatTwaTackMeasureValue(row.port, row.measure),
      stbd: formatTwaTackMeasureValue(row.stbd, row.measure),
      delta: formatTwaTackMeasureValue(row.delta, row.measure),
      portCount: row.portCount,
      stbdCount: row.stbdCount,
    }));
  }
  return stats.rows.map((row) => ({
    key: row.sensor,
    label: row.label,
    port: formatTwaTackValue(row.port, stats.metric),
    stbd: formatTwaTackValue(row.stbd, stats.metric),
    delta: formatTwaTackDelta(row.delta),
    portCount: row.portCount,
    stbdCount: row.stbdCount,
  }));
}

export interface LiveTwaTackTableProps {
  /** Source to read, by name. Defaults to {@link DEFAULT_TWA_TACK_SOURCE}. */
  sourceName?: string;
}

function formatWindow(ms: number): string {
  const minutes = ms / 60000;
  if (minutes >= 1) return `${Number.isInteger(minutes) ? minutes : minutes.toFixed(1)} min`;
  return `${Math.round(ms / 1000)} s`;
}

/**
 * Why the cells are blank. Shown in-panel rather than hiding the overlay — a blank map corner gives no
 * clue whether the channel is missing, the grade filter bit, or the boat is reaching.
 */
function describeGap(
  points: readonly Record<string, unknown>[],
  upwind: AnyModeStats,
  downwind: AnyModeStats,
  view: TwaTackView
): string | null {
  if (points.length === 0) return "No stream data in the last 5 min";

  const sensorView = isTwaTackSensorView(view);
  const label = sensorView
    ? `${TWA_TACK_SENSOR_LABELS[view]} TWS/BSP/TWA`
    : TWA_TACK_METRIC_LABELS[view];
  const resolved = [...upwind.rows, ...downwind.rows].map((r) => r.channel).filter(Boolean);
  if (resolved.length === 0) {
    const seen = listTwaChannels(points, sensorView ? "sensor" : view);
    return seen.length
      ? `No ${sensorView ? label : `bow/MHU ${label}`} channel — stream has ${seen.join(", ")}`
      : `No ${label} channels in stream`;
  }

  if (upwind.sampleCount === 0 && downwind.sampleCount === 0) {
    return upwind.rejectedByGrade + downwind.rejectedByGrade > 0
      ? "No grade-3 samples upwind or downwind"
      : "No upwind/downwind samples yet";
  }

  const hasValue = [...upwind.rows, ...downwind.rows].some((r) => r.port !== null || r.stbd !== null);
  return hasValue ? null : "Not enough samples per tack yet";
}

/**
 * One band's table. The first column heads with the view name — the metric when both sensors are
 * compared, the sensor when its three measures are listed — so the caption and header together read
 * "UPWIND / BOW" without any extra chrome.
 */
function ModeTable(props: { band: string; view: TwaTackView; stats: AnyModeStats }) {
  const viewLabel = () => twaTackViewLabel(props.view);
  const rows = createMemo(() => toDisplayRows(props.stats));
  return (
    <table class="live-twa-tack__grid">
      <caption class="live-twa-tack__caption">{`${props.band} ${viewLabel()}`}</caption>
      <thead>
        <tr>
          <th class="live-twa-tack__th live-twa-tack__th--sensor">{viewLabel()}</th>
          <th class="live-twa-tack__th live-twa-tack__th--port">PORT</th>
          <th class="live-twa-tack__th live-twa-tack__th--stbd">STBD</th>
          <th class="live-twa-tack__th live-twa-tack__th--delta">DELTA</th>
        </tr>
      </thead>
      <tbody>
        <For each={rows()}>
          {(row) => (
            <tr class="live-twa-tack__row">
              <td class="live-twa-tack__td live-twa-tack__td--sensor">{row.label}</td>
              <td
                class="live-twa-tack__td live-twa-tack__td--value"
                style={{ color: PORT_COLOR }}
                title={`${row.portCount} samples`}
              >
                {row.port}
              </td>
              <td
                class="live-twa-tack__td live-twa-tack__td--value"
                style={{ color: STBD_COLOR }}
                title={`${row.stbdCount} samples`}
              >
                {row.stbd}
              </td>
              <td class="live-twa-tack__td live-twa-tack__td--delta">{row.delta}</td>
            </tr>
          )}
        </For>
      </tbody>
    </table>
  );
}

/**
 * Live map overlay: per-tack means over the last {@link TWA_TACK_WINDOW_MS} of sailing, upwind and
 * downwind side by side, for one boat. Double-clicking cycles four views —
 * TWA (bow vs masthead) → TWD (bow vs masthead) → BOW (TWS/BSP/TWA) → MHU (TWS/BSP/TWA) → TWA.
 *
 * All four average the same fetched rows, so a view change is instant and never changes which
 * samples count. Speeds follow the app-wide knots/metres preference.
 *
 * Data comes from the streaming merged endpoint (server-side recent store) rather than the columnar
 * Arrow socket: the per-sensor bow/MHU channels are not in LIVE_STREAM_CHANNELS, and the browser ring
 * buffers only hold ~2 minutes right after a (re)connect. Polled on a slow interval — see
 * {@link TWA_TACK_REFRESH_MS}.
 */
export default function LiveTwaTackTable(props: LiveTwaTackTableProps) {
  const [rowsState, setRowsState] = createSignal<TwaTackRows | null>(null);
  const [view, setView] = createSignal<TwaTackView>("twa");
  const [lastUpdatedMs, setLastUpdatedMs] = createSignal(0);
  const [loading, setLoading] = createSignal(false);
  const [nowMs, setNowMs] = createSignal(Date.now());

  let isMounted = true;
  onCleanup(() => {
    isMounted = false;
  });

  const wantedName = createMemo(() => (props.sourceName || DEFAULT_TWA_TACK_SOURCE).trim());

  /** Resolve the wanted source by name (case-insensitive) once the source catalogue is loaded. */
  const target = createMemo(() => {
    if (!sourcesStore.isReady()) return null;
    const wanted = wantedName().toLowerCase();
    if (!wanted) return null;
    const match = sourcesStore
      .sources()
      .find((s: any) => String(s?.source_name ?? "").trim().toLowerCase() === wanted);
    if (!match) return null;
    return {
      sourceId: Number(match.source_id),
      sourceName: String(match.source_name).trim(),
      color: match.color || "#1f77b4",
    };
  });

  const loadStats = async (sourceName: string) => {
    setLoading(true);
    try {
      const endTime = Date.now();
      let rows: Record<string, unknown>[] = [];
      let windowMs = TWA_TACK_WINDOW_MS;

      for (const candidateMs of TWA_TACK_FALLBACK_WINDOWS_MS) {
        const points = await streamingDataService.fetchMergedData(
          sourceName,
          TWA_TACK_CHANNELS,
          endTime - candidateMs,
          endTime
        );
        windowMs = candidateMs;
        if (points.length > 0) {
          rows = points as Record<string, unknown>[];
          break;
        }
      }

      if (!isMounted) return;
      setRowsState({ rows, windowMs });
      setLastUpdatedMs(Date.now());
      setNowMs(Date.now());
    } finally {
      if (isMounted) setLoading(false);
    }
  };

  /**
   * Unit for the TWS and BSP rows, taken from the project's default speed channel — `Bsp_kph` on
   * GP50, so those rows read kph. This is the convention the rest of the app displays in, including
   * the BSP panel on this same map; a user-level knots preference here would have the two overlays
   * disagreeing on the same screen.
   */
  const displayUnit = createMemo(() => twaTackUnitFromChannel(defaultChannelsStore.bspName()));

  /**
   * Stats for the active view. Derived rather than computed in {@link loadStats} so cycling views —
   * or flipping the units toggle — re-averages the rows already in memory instead of waiting out the
   * poll interval.
   */
  const snapshot = createMemo<TwaTackSnapshot | null>(() => {
    const state = rowsState();
    if (!state) return null;
    const active = view();
    const unit = displayUnit();
    const forMode = (mode: "upwind" | "downwind"): AnyModeStats =>
      isTwaTackSensorView(active)
        ? computeTwaTackSensorStats(state.rows, mode, active, unit)
        : computeTwaTackStats(state.rows, mode, active);

    const upwind = forMode("upwind");
    const downwind = forMode("downwind");
    return {
      view: active,
      upwind,
      downwind,
      note: describeGap(state.rows, upwind, downwind, active),
      windowMs: state.windowMs,
    };
  });

  // Diagnostics for whatever is on screen. Verbose logging is easy to miss in a busy live console, so
  // the latest run is also parked on window — read it with `__twaTack` (`.sampleRows` for raw shape).
  createEffect(() => {
    const snap = snapshot();
    const state = rowsState();
    if (!snap || !state) return;
    const active = snap.view;
    const sensorView = isTwaTackSensorView(active);
    const cells = [...snap.upwind.rows, ...snap.downwind.rows];
    const diagnostic = {
      source: untrack(() => target()?.sourceName ?? wantedName()),
      view: active,
      displayUnit: displayUnit(),
      windowMs: snap.windowMs,
      rows: state.rows.length,
      upwindSamples: snap.upwind.sampleCount,
      downwindSamples: snap.downwind.sampleCount,
      upwindRejectedByGrade: snap.upwind.rejectedByGrade,
      downwindRejectedByGrade: snap.downwind.rejectedByGrade,
      resolvedChannels: cells.map((r) => r.channel).filter(Boolean),
      twaChannelsSeen: listTwaChannels(state.rows, sensorView ? "sensor" : active),
      rowCoverage: summariseRowCoverage(state.rows),
      cells: cells.map((r) => ({
        row: "measure" in r ? r.measure : r.sensor,
        portCount: r.portCount,
        stbdCount: r.stbdCount,
      })),
      note: snap.note,
    };
    debug("[LiveTwaTackTable] stats refreshed", diagnostic);
    if (typeof window !== "undefined") {
      (window as any).__twaTack = { ...diagnostic, sampleRows: state.rows.slice(0, 3) };
    }
  });

  /**
   * Slow poll. The tick body runs inside {@link untrack} so signal reads (loading, sources) do not
   * subscribe this effect — otherwise each completed load would restart the interval and refire.
   */
  createEffect(() => {
    const resolved = target();
    if (!resolved) {
      setRowsState(null);
      return;
    }
    const sourceName = resolved.sourceName;

    const tick = () => {
      untrack(() => {
        if (!isMounted || loading()) return;
        if (typeof document !== "undefined" && document.hidden) return;
        void loadStats(sourceName);
      });
    };

    tick();
    const intervalId = window.setInterval(tick, TWA_TACK_REFRESH_MS);
    // A hidden tab skips its ticks; refresh as soon as it comes back rather than waiting a full period.
    const onVisible = () => {
      if (typeof document !== "undefined" && !document.hidden) tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    onCleanup(() => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    });
  });

  // Drives the stale styling between reloads.
  createEffect(() => {
    if (!target()) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 5000);
    onCleanup(() => window.clearInterval(id));
  });

  const isStale = createMemo(() => {
    const updated = lastUpdatedMs();
    if (!updated) return false;
    return nowMs() - updated > TWA_TACK_STALE_MS;
  });

  /** What the active view is averaging, spelled out for the hover title. */
  const quantityLabel = (v: TwaTackView): string => {
    if (v === "twd") return "mean TWD";
    if (v === "twa") return "mean |TWA|";
    // The rows are labelled bare, so name the speed unit here — this is the only place it appears.
    return `${TWA_TACK_SENSOR_LABELS[v]} mean TWS/BSP in ${displayUnit()}, |TWA| in deg`;
  };

  /** Kept off-screen: the panel is intentionally label-free, so context lives on hover. */
  const contextLabel = createMemo(() => {
    const snap = snapshot();
    const window = snap ? formatWindow(snap.windowMs) : formatWindow(TWA_TACK_WINDOW_MS);
    const suffix = loading() ? " · updating…" : isStale() ? " · stale" : "";
    const reason = snap?.note ? ` · ${snap.note}` : "";
    const next = nextTwaTackView(view());
    return `${wantedName()} · ${quantityLabel(view())} · last ${window}${suffix}${reason} · double-click for ${quantityLabel(next)}`;
  });

  return (
    <div
      class={`live-twa-tack${isStale() ? " live-twa-tack--stale" : ""}`}
      role="region"
      aria-label={contextLabel()}
      title={contextLabel()}
      /*
       * `on:` (native listener) rather than onDblClick: Solid delegates dblclick from document, so a
       * delegated handler's stopPropagation fires too late to keep the event off the Mapbox container,
       * whose own dblclick zooms the live map to fit (LiveMapContainer).
       */
      on:dblclick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setView(nextTwaTackView);
      }}
    >
      {/* Deliberately label-free beyond the captions. Source, window and any reason the cells are
          blank live on the hover title and in the console debug line. */}
      <Show when={snapshot()}>
        {(snap) => (
          <div class="live-twa-tack__tables">
            <ModeTable band="UPWIND" view={snap().view} stats={snap().upwind} />
            <ModeTable band="DOWNWIND" view={snap().view} stats={snap().downwind} />
          </div>
        )}
      </Show>
    </div>
  );
}
